/**
 * Copyright (C) 2023 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

import Contract from '../../shared/entities/contract';
import { Action, ActionCreators } from './actions';

export interface State {
    readonly contracts: Contract[] | undefined;
    readonly isRequestingContracts: boolean;
    readonly isContractsRequestFinished: boolean;
    readonly contractsRequestError?: string;

    readonly currentContract: Contract | undefined;
    readonly isRequestingCurrentContract: boolean;
    readonly isCurrentContractRequestFinished: boolean;
    readonly currentContractRequestError?: string;

    readonly isProvisioningContract: boolean;
    readonly isContractProvisioningFinished: boolean;
    readonly contractProvisioningError?: string;

    readonly isDeletingContract: boolean;
    readonly isDeletingContractFinished: boolean;
    readonly deleteContractError?: string;

    readonly isUpdatingContract: boolean;
    readonly isUpdatingContractFinished: boolean;
    readonly updateContractError?: string;
}

export const initialContractProvisioningState: State = {
    contracts: undefined,
    isRequestingContracts: false,
    isContractsRequestFinished: false,
    contractsRequestError: undefined,
    
    currentContract: undefined,
    isRequestingCurrentContract: false,
    isCurrentContractRequestFinished: false,
    currentContractRequestError: undefined,

    isProvisioningContract: false,
    isContractProvisioningFinished: false,
    contractProvisioningError: undefined,

    isDeletingContract: false,
    isDeletingContractFinished: false,
    deleteContractError: undefined,

    isUpdatingContract: false,
    isUpdatingContractFinished: false,
    updateContractError: undefined,
};

export default function reducer(state: State = initialContractProvisioningState, action: Action): State {
  let partialState: Partial<State> | undefined;

  switch (action.type) {
    // For requesting list of provisioned contracts
    case ActionCreators.RequestContracts.type:
        partialState = {
            contracts: undefined,
            isRequestingContracts: true,
            isContractsRequestFinished: false,
            contractsRequestError: undefined,
        };
        break;

    case ActionCreators.ReceivedContractsSuccess.type: {
        partialState = {
            contracts: action.payload.response,
            isRequestingContracts: false,
            isContractsRequestFinished: true,
            contractsRequestError: undefined,
        };
        break;
      }

    case ActionCreators.ReceivedContractsError.type:
        partialState = {
            isRequestingContracts: false,
            isContractsRequestFinished: true,
            contractsRequestError: action.payload.message,
        };
        break;

    case ActionCreators.ClearReceivedContractsError.type:
        partialState = {
            contractsRequestError: undefined,
        };
        break;
    
    // For requesting specific provisioned contract
    case ActionCreators.RequestCurrentContract.type:
        partialState = {
            currentContract: undefined,
            isRequestingCurrentContract: true,
            isCurrentContractRequestFinished: false,
            currentContractRequestError: undefined,
        };
        break;

    case ActionCreators.ReceivedCurrentContractSuccess.type: {
        partialState = {
            currentContract: action.payload.response,
            isRequestingCurrentContract: false,
            isCurrentContractRequestFinished: true,
            currentContractRequestError: undefined,
        };
        break;
    }

    case ActionCreators.ReceivedCurrentContractError.type:
        partialState = {
            isRequestingCurrentContract: false,
            isCurrentContractRequestFinished: true,
            currentContractRequestError: action.payload.message,
        };
        break;
    
    case ActionCreators.ClearReceivedCurrentContractError.type:
        partialState = {
            currentContractRequestError: undefined,
        };
        break;
    
    // For adding a contract
    case ActionCreators.ProvisionContract.type:
        partialState = {
            isProvisioningContract: true,
            isContractProvisioningFinished: false,
            contractProvisioningError: undefined,
        };
        break;

    case ActionCreators.ContractProvisioningSuccess.type:
        partialState = {
            isProvisioningContract: false,
            isContractProvisioningFinished: true,
            contractProvisioningError: undefined,
            currentContract: action.payload,
        };
        break;

    case ActionCreators.ContractProvisioningError.type:
        partialState = {
            isProvisioningContract: false,
            isContractProvisioningFinished: true,
            contractProvisioningError: action.payload.message,
        };
        break;
        
    case ActionCreators.ClearContractProvisioningError.type:
        partialState = {
            contractProvisioningError: undefined,
        };
        break;

    // For deleting a contract
    case ActionCreators.DeleteContract.type:
        partialState = {
            isDeletingContract: true,
            isDeletingContractFinished: false,
            deleteContractError: undefined,
        };
        break;

    case ActionCreators.DeleteContractSuccess.type:
        partialState = {
            isDeletingContract: false,
            isDeletingContractFinished: true,
            deleteContractError: undefined,
        };
        break;

    case ActionCreators.DeleteContractError.type:
        partialState = {
            isDeletingContract: false,
            isDeletingContractFinished: true,
            deleteContractError: action.payload.message,
        };
        break;

    case ActionCreators.ClearDeleteContractError.type:
        partialState = {
            deleteContractError: undefined,
        };
        break;

    // For updating a contract
    case ActionCreators.UpdateContract.type:
        partialState = {
            isUpdatingContract: true,
            isUpdatingContractFinished: false,
            updateContractError: undefined,
        };
        break;

    case ActionCreators.UpdateContractSuccess.type:
        partialState = {
            isUpdatingContract: false,
            isUpdatingContractFinished: true,
            updateContractError: undefined,
            currentContract: action.payload,
        };
        break;

    case ActionCreators.UpdateContractError.type:
        partialState = {
            isUpdatingContract: false,
            isUpdatingContractFinished: true,
            updateContractError: action.payload.message,
        };
        break;

    case ActionCreators.ClearUpdateContractError.type:
        partialState = {
            updateContractError: undefined,
        };
        break;

    default:
      return state;
  }
  return { ...state, ...partialState };
}
